/**
 * Utility functions for Group Management
 */

/** Format a number with commas and decimals */
export const formatNumber = (number, decimals = 2) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number || 0);
};

/** Format a value as USD currency */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

/** Format a date string to readable format */
export const formatDate = (date) => {
  if (!date) return 'Unknown';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/** Extract the group ID regardless of field name */
export const getGroupId = (group) => {
  if (!group) return null;
  return group._id || group.groupId || group.id;
};

/** Extract data from various API response shapes */
export const extractData = (apiResponse) => {
  if (!apiResponse) return null;
  if (apiResponse.data?.data) return apiResponse.data.data;
  if (apiResponse.data) return apiResponse.data;
  return apiResponse;
};

/** Extract array from API response */
export const extractArray = (apiResponse) => {
  if (!apiResponse) return [];
  if (Array.isArray(apiResponse)) return apiResponse;
  if (Array.isArray(apiResponse.data?.data)) return apiResponse.data.data;
  if (Array.isArray(apiResponse.data)) return apiResponse.data;
  if (apiResponse.data?.data?.users && Array.isArray(apiResponse.data.data.users)) return apiResponse.data.data.users;
  if (apiResponse.data?.users && Array.isArray(apiResponse.data.users)) return apiResponse.data.users;
  return [];
};

/** Get deep nested field value */
export const deepGet = (obj, path, defaultValue = null) => {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return defaultValue;
    current = current[key];
  }
  return current !== undefined && current !== null ? current : defaultValue;
};

/** Categorize an instrument symbol */
const CATEGORY_ORDER = ['Forex', 'Crypto', 'Commodities', 'Indices', 'Stocks'];

const CRYPTO_SYMBOLS = [
  'BTC', 'ETH', 'LTC', 'XRP', 'SOL', 'USDT', 'USDC', 'BNB', 'ADA',
  'DOT', 'LINK', 'AVAX', 'MATIC', 'ATOM', 'UNI', 'AAVE', 'COMP',
  'MKR', 'SNX', 'YFI', 'CRV',
];

const COMM_SYMBOLS = [
  'GOLD', 'SILVER', 'OIL', 'NATGAS', 'COPPER', 'PLATINUM',
  'PALLADIUM', 'WHEAT', 'CORN', 'SOYBEAN', 'XAU', 'XAG',
];

const INDEX_SYMBOLS = [
  'SP500', 'NASDAQ', 'DOW', 'FTSE', 'DAX', 'NIKKEI', 'HANGSENG', 'S&P',
];

export const getInstrumentCategory = (symbol, availableMap = {}) => {
  // Use map-provided type if available
  if (availableMap[symbol]?.type && availableMap[symbol].type !== 'Other') {
    return availableMap[symbol].type;
  }
  const upper = symbol.toUpperCase();
  // Forex: slash-pair format, 6-letter pairs, or suffixes
  if (
    symbol.includes('/') ||
    symbol.includes('.FX.') ||
    /^[A-Z]{6}$/.test(symbol)
  ) return 'Forex';
  if (CRYPTO_SYMBOLS.some((c) => upper.includes(c))) return 'Crypto';
  if (COMM_SYMBOLS.some((c) => upper.includes(c))) return 'Commodities';
  if (INDEX_SYMBOLS.some((c) => upper.includes(c))) return 'Indices';
  return 'Stocks';
};

export { CATEGORY_ORDER };
